import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  getDefaultRateMinimumLengthOfStayRuleAsync,
  selectMinimumLengthOfStayRuleDetails,
  selectMinimumLengthOfStayRuleDetailsLoadingStatus,
  updateDefaultRateMinimumLengthOfStayRuleAsync,
} from '../defaultRateRulesSlice';
import RateMinimumLengthOfStayForm from './RateMinimumLengthOfStayForm';
import { MinimumLengthOfStayRuleDetails } from '../../../common/types/rates.types';
import {
  copyMinimumLengthOfStayRuleToSeasons,
  deleteRulesFromSeasonsAsync,
} from '../../defaultRateTools/defaultRateToolsSlice';
import { formatDateToFinnish } from '../../../common/utils/dateUtils';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';
import { SEASON_RATE_RULE_TYPE } from '../../../common/constants';

function DefaultRateMinimumLengthOfStay({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const minimumLengthOfStayRuleDetails = useAppSelector(
    selectMinimumLengthOfStayRuleDetails,
  );
  const minimumLengthOfStayItemStatus = useAppSelector(
    selectMinimumLengthOfStayRuleDetailsLoadingStatus,
  );

  const initialData: MinimumLengthOfStayRuleDetails = {
    defaultRateId,
    minimumLengthOfStay: null,
    isActive: true,
    isCopyable: true,
  };

  const [data, setData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [copyStartDate, setCopyStartDate] = useState<Date | null>(null);
  const [copyEndDate, setCopyEndDate] = useState<Date | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    dispatch(getDefaultRateMinimumLengthOfStayRuleAsync(defaultRateId));
  }, [defaultRateId, dispatch]);

  useEffect(() => {
    if (minimumLengthOfStayRuleDetails && minimumLengthOfStayRuleDetails.id) {
      setData(minimumLengthOfStayRuleDetails);
    } else {
      setData(initialData);
    }
  }, [minimumLengthOfStayRuleDetails]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setData(newData);
  };

  const onSubmit = () => {
    dispatch(updateDefaultRateMinimumLengthOfStayRuleAsync(data));
    setSubmitted(true);
  };

  const onDelete = () => {
    if (
      window.confirm('Haluatko varmasti poistaa säännöt valituilta kausilta?')
    ) {
      dispatch(
        deleteRulesFromSeasonsAsync({
          accommodationId,
          selectedSeasonRateIds,
          ruleType: SEASON_RATE_RULE_TYPE.MinimumLengthOfStayRule,
        }),
      );
      setSubmitted(false);
      setData(initialData);
      setDeleted(true);
    }
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyMinimumLengthOfStayRuleToSeasons({
          accommodationId,
          // copyStartDate: formatDateToFinnish(copyStartDate),
          // copyEndDate: formatDateToFinnish(copyEndDate),
          selectedSeasonRateIds,
          minimumLengthOfStay: data.minimumLengthOfStay,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  return (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <RateMinimumLengthOfStayForm
        loadingStatus={minimumLengthOfStayItemStatus}
        handleInputChange={handleInputChange}
        onSubmit={onSubmit}
        onDelete={onDelete}
        submitted={submitted}
        deleted={deleted}
        data={data}
        hasWriteAccess={hasWriteAccess}
        isDefaultRate
      />
      <Form.Group className="mb-24">
        {/*
        <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyStartDate(date)}
            className="form-control"
            selected={copyStartDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyEndDate(date)}
            className="form-control"
            selected={copyEndDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Text muted>
          Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
          päättymispäivämäärää sekä loppupäivä on kopioinnin alkamispäivämäärän
          jälkeen.
        </Form.Text>
        */}
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateMinimumLengthOfStay;
